// SidebarXL - Sprint 44 XL.
// Barra lateral con grupos de navegacion (desde navModel.js). Usa NavLink
// para estado activo; aplica tokens via CSS vars; dark mode via
// [data-xl-theme="dark"]. Grupos con encabezado; items con icono simbolico
// + label.
//
// No controla colapso responsive: eso lo decide ShellXL (grid). Este
// componente ocupa 100% de su columna.
import { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { ensureKeyframes, injectCSS } from '../../design/motion';
import LogoMark from '../../branding/LogoMark';
import navGroups from './navModel';

const CSS_ID = 'xl-sidebar-css';
const CSS = `
.xl-side {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--xl-color-surface);
  border-right: 1px solid var(--xl-color-border);
  color: var(--xl-color-text);
  font-family: var(--xl-font-sans);
  overflow: hidden;
}
.xl-side__head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 18px;
  border-bottom: 1px solid var(--xl-color-border);
  min-height: 64px;
  box-sizing: border-box;
}
.xl-side__brand {
  font-weight: 700;
  font-size: 15px;
  letter-spacing: -0.01em;
  color: var(--xl-color-text);
  line-height: 1.2;
}
.xl-side__brand-sub {
  font-size: 11px;
  color: var(--xl-color-text-muted);
  font-weight: 500;
  margin-top: 2px;
}

.xl-side__scroll { flex: 1; overflow-y: auto; padding: 12px 10px 24px }

.xl-side__group { margin-bottom: 14px }
.xl-side__group-hd {
  padding: 8px 10px 4px;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--xl-color-text-muted);
  font-weight: 700;
}

.xl-side__item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  margin: 1px 0;
  border-radius: var(--xl-radius-md);
  color: var(--xl-color-text-secondary);
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  transition: background 120ms cubic-bezier(0,0,0.2,1),
              color      120ms cubic-bezier(0,0,0.2,1);
}
.xl-side__item:hover {
  background: var(--xl-color-surface-alt);
  color: var(--xl-color-text);
}
.xl-side__item:focus-visible {
  outline: 2px solid var(--xl-color-focus);
  outline-offset: 2px;
}
.xl-side__item.xl-side__item--active {
  background: rgba(37,99,235,0.10);
  color: var(--xl-color-info);
  font-weight: 600;
}
[data-xl-theme="dark"] .xl-side__item.xl-side__item--active {
  background: rgba(96,165,250,0.14);
  color: var(--xl-color-info);
}
.xl-side__item-ico {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  line-height: 1;
  color: currentColor;
}

.xl-side__foot {
  padding: 10px 18px;
  border-top: 1px solid var(--xl-color-border);
  font-size: 11px;
  color: var(--xl-color-text-muted);
  display: flex;
  align-items: center;
  justify-content: space-between;
}
`;

// Glifos simbolicos (sin libs). Un sub-conjunto minimo suficiente para
// distinguir areas. Fallback: primera letra del label.
const ICONS = {
  home:     '\u2302', // HOUSE
  boxes:    '\u25A3', // BOX WITH DOT
  tag:      '\u29C5', // SQUARED TIMES  (placeholder simbolico)
  scan:     '\u25A6', // HATCHED SQUARE
  pkg:      '\u25A2', // WHITE SQUARE W ROUNDED
  po:       '\u2630', // TRIGRAM FOR HEAVEN (hamburguesa)
  grn:      '\u2261', // IDENTICAL TO
  ship:     '\u2693', // ANCHOR
  truck:    '\u25B6', // PLAY
  supplier: '\u2692', // HAMMER
  users:    '\u263B', // BLACK SMILING FACE
  doc:      '\u25A7', // SQUARED UPPER LEFT
  sale:     '\u20A9', // WON SIGN
  spark:    '\u2728', // sparkles
  book:     '\u29C7', // SQUARED SMALL CIRCLE
  cog:      '\u2699', // GEAR
};

function Icon({ name }) {
  const ch = ICONS[name] || '\u25CF';
  return <span className="xl-side__item-ico" aria-hidden="true">{ch}</span>;
}

export default function SidebarXL({ version = 'v0.2.0-xl' }) {
  useEffect(() => {
    ensureKeyframes();
    injectCSS(CSS_ID, CSS);
  }, []);

  return (
    <aside className="xl-side" aria-label="Navegacion principal">
      <div className="xl-side__head">
        <LogoMark size={32} />
        <div>
          <div className="xl-side__brand">StockPilot ERP</div>
          <div className="xl-side__brand-sub">Experience Layer</div>
        </div>
      </div>

      <nav className="xl-side__scroll" aria-label="Menu">
        {navGroups.map((g) => (
          <div key={g.id} className="xl-side__group">
            <div className="xl-side__group-hd">{g.label}</div>
            {g.items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                className={({ isActive }) =>
                  'xl-side__item' + (isActive ? ' xl-side__item--active' : '')
                }
              >
                <Icon name={it.icon} />
                <span>{it.label}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div className="xl-side__foot">
        <span>{version}</span>
        <span>XL</span>
      </div>
    </aside>
  );
}
